import { createSelector } from 'reselect';
import _ from 'lodash';
import moment from 'moment';
import natsort from 'natsort';

import { format } from '@breezeway/brzw-react-core';
import { helpers as calendarHelpers } from '@common/brzw_calendar';
import {
  getTaskHomeId,
  getFilterStatuses,
  shouldTaskAssignmentDisplay,
  shouldTaskAssignmentStatusDisplay,
  shouldTaskStatusDisplay,
  sortTasks,
  VALID_TASK_FILTERS,
  TASK_AVAILABILITY_STATUS,
} from '@common/utils/task';
import { DEPARTMENTS } from '@common/utils/departments';
import { isNotAvailable, isOverbooked, isNearCapacity } from '@common/utils/assignment_bar';
import { groupReservations } from '@common/utils/reservations';
import { personName, filterAssignable } from '@common/utils/people_helpers';
import { searchObjects } from '@common/utils/search';
import { getParentGroup } from '@common/utils/company';
import datastore from '@datastore';

import { getActiveFilters } from '@common/utils/filters';

import { shouldPropertyBeFiltered } from '@common/utils/filters/property';
import { PROPERTY_FILTERS_NAMES_SCHEDULE } from '@common/utils/filters/constants';

import {
  PAGINATION_SETTINGS,
  SCHEDULE_MODE,
  SCHEDULE_DATE_MODIFIER,
  SCHEDULE_ROUTE_DATE_FORMAT,
  SCHEDULE_UNASSIGNED_ID,
  SCHEDULE_SUBJECT,
} from './constants';
import * as ScheduleHelpers from './helpers';

const { formatDate } = format.datetime;
const sorterFunction = natsort();

/**
 * @return {Boolean}
 */
const isPersonTypeSchedule = () => {
  const pathname = _.get(window, 'location.pathname', '');
  const isPersonPlanningType = ScheduleHelpers.isPersonPlanningScheduleLocation(pathname);
  const isPersonType = ScheduleHelpers.isPersonScheduleLocation(pathname);

  return isPersonPlanningType || isPersonType;
};

export const getFilters = state => state.schedule.filters;

export const getScheduleFiltersAppliedCount = createSelector(
  getFilters,
  scheduleFilters => _.size(_.flatten(_.values(scheduleFilters))),
);

export const {
  getTaskAssignedPeople,
  company: { getAllPeopleAvailabilityFromSixteenHours, getPersonAvailabilityByDOWFromSixteenHours },
} = datastore.selectors;

const getFromScheduleConfig = (state, path) =>
  _.get(state, `internalStore.scheduleConfig.${path}`, null);
export const getActiveRangeDate = state => getFromScheduleConfig(state, 'dates');

export const getMonthlySummary = state => state.schedule.monthlySummary;

const getPropertyReservationIds = state => state.schedule.propertyReservations;

const getPropertyTasksByDate = state => state.schedule.propertyTasksByDate;
const getPropertyTaskDraftsByDate = state => state.schedule.propertyTaskDraftsByDate;

const getPersonTasksByDate = state => state.schedule.personTasksByDate;
const getPersonTaskDraftsByDate = state => state.schedule.personTaskDraftsByDate;

export const getTasksSummary = state => state.schedule.tasksSummary;

export const getCalendarVisibility = state => state.internalStore.scheduleConfig.calendarVisible;
export const getDisplayedScheduleType = state =>
  state.internalStore.scheduleConfig.displayedScheduleType;

export const getNextReservations = state => state.schedule.nextReservations;
export const propertyIsReady = (nextReservations, propertyId) =>
  _.get(nextReservations, [propertyId, 'ready'], null);
export const getPropertyNextReservation = (state, propertyId) =>
  _.get(getNextReservations(state), propertyId);

export const isDateInTimeframe = (date, startDate, endDate) =>
  moment(date).isBetween(moment(startDate), moment(endDate), null, '[]');

const getFilterObject = (filters, filterType) => {
  const filtersByType = _.get(filters, filterType);
  return _.zipObject(filtersByType, _.map(_.range(_.size(filtersByType)), () => true));
};

export const getPropertyReservations = (state, propertyId) =>
  _.map(_.get(getPropertyReservationIds(state), propertyId), reservationId =>
    datastore.selectors.getReservation(state, reservationId),
  );

const getAllPropertyReservations = createSelector(
  getPropertyReservationIds,
  datastore.selectors.getReservations,
  (propertyReservationIds, reservations) => {
    const result = {};
    _.forEach(propertyReservationIds, (reservationIds, propertyId) => {
      result[propertyId] = _.map(reservationIds, reservationId =>
        _.get(reservations, reservationId),
      );
    });
    return result;
  },
);

const taskShouldDisplay = (task, filters) => {
  if (_.size(filters.departments) > 0) {
    if (
      !_.includes(filters.departments, 'hasTask') &&
      !_.includes(filters.departments, _.get(task, 'type_department.code'))
    ) {
      return false;
    }
  }

  if (_.size(filters.statuses) > 0) {
    if (!shouldTaskStatusDisplay(task, filters.statuses)) {
      return false;
    }
  }

  if (_.size(filters.assignments) > 0) {
    if (!shouldTaskAssignmentDisplay(task, filters.assignments)) {
      return false;
    }
  }

  if (_.size(filters.assignmentStatus) > 0) {
    if (!shouldTaskAssignmentStatusDisplay(task, filters.assignmentStatus)) {
      return false;
    }
  }

  return true;
};

const getDatesInRange = (startDate, endDate) => {
  const dates = [];
  const currentMoment = moment(startDate);
  const endMoment = moment(endDate);
  while (currentMoment <= endMoment) {
    dates.push(currentMoment.format('YYYY-MM-DD'));
    currentMoment.add(1, 'days');
  }
  return dates;
};

const filteredTasksByDate = (propertyTasksByDate, tasks, filters, dates) => {
  const { endDate, startDate } = dates;
  const dateRange = getDatesInRange(startDate, endDate);
  const filteredPropertyTasksByDate = {};

  _.forEach(propertyTasksByDate, (dateTasks, propertyId) => {
    const filteredDateTasks = {};
    _.forEach(dateRange, date => {
      if (_.has(dateTasks, date)) {
        // TODO - filter tasks
        const filteredTasks = _.filter(
          _.filter(_.map(dateTasks[date], taskId => _.get(tasks, taskId))),
          task => taskShouldDisplay(task, filters),
        );
        if (_.size(filteredTasks) > 0) {
          filteredDateTasks[date] = filteredTasks;
        }
      }
    });
    if (_.size(filteredDateTasks) > 0) {
      filteredPropertyTasksByDate[propertyId] = filteredDateTasks;
    }
  });
  return filteredPropertyTasksByDate;
};

export const getFilteredScheduledPropertyTasksByDate = createSelector(
  getPropertyTasksByDate,
  datastore.selectors.getTasks,
  getFilters,
  getActiveRangeDate,
  filteredTasksByDate,
);

const getFilteredTaskPropertyIds = createSelector(
  getFilteredScheduledPropertyTasksByDate,
  propertyTasks => _.map(_.keys(propertyTasks), _.toInteger),
);

export const getFilteredScheduledPropertyTaskDraftsByDate = createSelector(
  getPropertyTaskDraftsByDate,
  datastore.selectors.getTaskDrafts,
  getFilters,
  getActiveRangeDate,
  filteredTasksByDate,
);

const getFilteredTaskDraftPropertyIds = createSelector(
  getFilteredScheduledPropertyTaskDraftsByDate,
  propertyTasks => _.map(_.keys(propertyTasks), _.toInteger),
);

export const getReservationDetailsForDate = (state, propertyId, date) => {
  const reservations = getPropertyReservations(state, propertyId);
  const nextReservation = getPropertyNextReservation(state, propertyId);

  const results = {
    checkin: null,
    checkout: null,
    occupied: null,
  };

  _.forEach(reservations, reservation => {
    if (reservation.checkin_date === date) {
      results.checkin = reservation;
      if (nextReservation && reservation.id === nextReservation.id) {
        results.checkin.ready = nextReservation.ready;
      }
    }

    if (reservation.checkout_date === date) {
      results.checkout = reservation;
    }

    if (reservation.checkin_date < date && reservation.checkout_date > date) {
      results.occupied = reservation;
    }
  });

  return results;
};

export const getWorkflowsToAddToReadiness = state => {
  if (!datastore.selectors.company.propertyReadinessWorkflowsIsEnabled(state)) {
    return datastore.selectors.company.getSortedWorkflows(state);
  }

  const workflowIds = _.map(datastore.selectors.company.getReadinessWorkflows(state), wf => wf.id);
  const workflowsToAdd = _.omit(datastore.selectors.company.getWorkflows(state), workflowIds);
  return _.sortBy(workflowsToAdd, wf => _.toLower(wf.name));
};

// Task Counts
const incrementCounter = (counts, type, value, incrementBy = 1) =>
  _.setWith(counts, [type, value], _.get(counts, [type, value], 0) + incrementBy, Object);

const getScheduleFilterCount = (counts, task, properties, homeTags, companyGroups) => {
  const taskAssignments = _.get(task, 'people', []);

  if (_.size(taskAssignments) > 0) {
    _.forEach(taskAssignments, taskPerson =>
      incrementCounter(counts, 'assignments', taskPerson.company_people_id),
    );
    incrementCounter(counts, 'assignments', 'assigned');
  } else {
    incrementCounter(counts, 'assignments', 'notAssigned');
  }

  const taskAssignmentsStatuses = _.uniq(
    _.map(_.get(task, 'people', []), person => _.get(person, 'type_task_user_status.code')),
  );
  _.each(taskAssignmentsStatuses, code => {
    incrementCounter(counts, 'assignmentStatus', code);
  });

  if (_.has(task, 'type_department.code')) {
    incrementCounter(counts, 'departments', task.type_department.code);
    incrementCounter(counts, 'departments', 'hasTask');
  }

  if (_.get(task, 'draft', false)) {
    incrementCounter(counts, 'statuses', 'draft');
  }

  // Task Status Counts
  _.forEach(getFilterStatuses(task), status => incrementCounter(counts, 'statuses', status));

  // property counts
  const property = _.get(properties, task.home_id);
  const groupId = _.get(property, 'company_group_id');

  if (_.get(property, 'building_id')) {
    incrementCounter(counts, 'buildings', property.building_id);
  }

  if (!isPersonTypeSchedule() && groupId) {
    incrementCounter(counts, 'groups', groupId);
    const parentGroup = getParentGroup(companyGroups, groupId);
    if (parentGroup) {
      incrementCounter(counts, 'groups', _.get(parentGroup, 'id'));
    }
  }

  if (_.has(homeTags, task.home_id)) {
    _.forEach(homeTags[task.home_id], tagId => incrementCounter(counts, 'tags', tagId));
  }
};

const setReservationCounts = (counts, reservations, dates, properties) => {
  const propertyIds = _.keys(properties);
  const reservationsByProperty = _.groupBy(reservations, 'home_id');

  _.forEach(propertyIds, propertyId => {
    if (!_.has(reservationsByProperty, propertyId)) {
      incrementCounter(counts, 'reservations', 'noReservation');
    }
  });

  _.forEach(reservationsByProperty, propertyReservations => {
    const { backToBack, checkins, checkouts, recent } = groupReservations(
      propertyReservations,
      dates,
    );

    const onHold = _.uniqBy(_.concat([], checkins.hold, checkouts.hold), 'id');

    const totalReservations = _.size(
      _.uniq([...backToBack.allIds, ...checkins.allIds, ...checkouts.allIds]),
    );
    incrementCounter(counts, 'reservations', 'hasReservation', totalReservations);
    incrementCounter(counts, 'reservations', 'recent', _.size(recent));
    incrementCounter(counts, 'reservations', 'backToBack', _.size(backToBack.all, 'date'));
    incrementCounter(counts, 'reservations', 'backToBackGuest', _.size(backToBack.guest));
    incrementCounter(counts, 'reservations', 'backToBackOwner', _.size(backToBack.owner));
    incrementCounter(counts, 'reservations', 'checkinOnly', _.size(checkins.all));
    incrementCounter(counts, 'reservations', 'checkoutOnly', _.size(checkouts.all));
    incrementCounter(counts, 'reservations', 'checkinOwner', _.size(checkins.owner));
    incrementCounter(counts, 'reservations', 'checkoutOwner', _.size(checkouts.owner));
    incrementCounter(counts, 'reservations', 'checkinGuest', _.size(checkins.guest));
    incrementCounter(counts, 'reservations', 'checkoutGuest', _.size(checkouts.guest));
    incrementCounter(counts, 'reservations', 'checkinHold', _.size(checkins.hold));
    incrementCounter(counts, 'reservations', 'checkoutHold', _.size(checkouts.hold));
    incrementCounter(counts, 'reservations', 'hold', _.size(onHold));
  });
};

const setPersonTypeScheduleCounts = (
  counts,
  people,
  groups,
  filters,
  filteredPersonTasks,
  filteredPersonTaskDrafts,
  tasks,
  taskDrafts,
  companyGroups,
) => {
  let housekeepingCounts = 0;
  let inspectionCounts = 0;
  let maintenanceCounts = 0;
  const groupsCounts = {};
  const allTasks = {
    ...tasks,
    ...taskDrafts,
  };

  _.each(allTasks, task => {
    const companyGroupId = _.get(task, 'home.company_group_id', '');
    const parentGroupId = _.get(getParentGroup(companyGroups, companyGroupId), 'id', null);

    if (!_.has(groupsCounts, companyGroupId)) {
      groupsCounts[companyGroupId] = 0;
    }

    groupsCounts[companyGroupId] += 1;

    if (parentGroupId) {
      if (!_.has(groupsCounts, parentGroupId)) {
        groupsCounts[parentGroupId] = 0;
      }

      groupsCounts[parentGroupId] += 1;
    }
  });

  _.each(people, person => {
    const personDepartments = _.get(person, 'type_departments', []);
    const personTasks = [
      ..._.get(filteredPersonTasks, person.id, []),
      ..._.get(filteredPersonTaskDrafts, person.id, []),
    ];
    const personTasksSize = _.size(personTasks);

    _.each(personDepartments, personDepartment => {
      const departmentCode = _.get(personDepartment, 'code', '');

      if (departmentCode === DEPARTMENTS.HOUSEKEEPING) {
        housekeepingCounts += personTasksSize;
      } else if (departmentCode === DEPARTMENTS.INSPECTION) {
        inspectionCounts += personTasksSize;
      } else if (departmentCode === DEPARTMENTS.MAINTENANCE) {
        maintenanceCounts += personTasksSize;
      }
    });
  });

  _.each(groupsCounts, (groupCounts, groupId) => {
    incrementCounter(counts, 'groups', groupId, groupCounts);
  });

  incrementCounter(counts, 'userDepartments', DEPARTMENTS.HOUSEKEEPING, housekeepingCounts);

  incrementCounter(counts, 'userDepartments', DEPARTMENTS.INSPECTION, inspectionCounts);

  incrementCounter(counts, 'userDepartments', DEPARTMENTS.MAINTENANCE, maintenanceCounts);
};

const setPlanningReservationCounts = (
  counts,
  reservations,
  dates,
  filteredPersonTasks,
  filteredPersonTaskDrafts,
) => {
  const tasks = _.flatten(_.values(filteredPersonTasks));
  const taskDrafts = _.flatten(_.values(filteredPersonTaskDrafts));
  const tasksPropertyIds = _.map(_.concat([], tasks, taskDrafts), 'home_id');
  const filteredReservations = {};

  _.forOwn(reservations, (reservation, reservationId) => {
    const propertyId = _.get(reservation, 'home_id', '');

    if (_.includes(tasksPropertyIds, propertyId)) {
      filteredReservations[reservationId] = reservation;
    }
  });

  setReservationCounts(counts, filteredReservations, dates);
};

const getReservationIdsByTypes = createSelector(
  datastore.selectors.getReservations,
  getActiveRangeDate,
  (reservations, dates) => {
    const reservationsByProperty = _.groupBy(reservations, 'home_id');
    const result = {
      backToBack: [],
      backToBackGuest: [],
      backToBackOwner: [],
      checkins: [],
      checkouts: [],
      checkinsOwner: [],
      checkoutsOwner: [],
      checkinsGuest: [],
      checkoutsGuest: [],
      hasReservation: [],
      hold: [],
      recent: [],
    };

    _.forEach(reservationsByProperty, propertyReservations => {
      const { backToBack, checkins, checkouts, recent } = groupReservations(
        propertyReservations,
        dates,
      );

      result.checkouts = [...result.checkouts, ...checkouts.allIds];
      result.checkins = [...result.checkins, ...checkins.allIds];
      result.backToBack = [...result.backToBack, ...backToBack.allIds];
      result.hasReservation = _.uniq([
        ...result.hasReservation,
        ...result.checkouts,
        ...result.checkins,
        ...result.backToBack,
      ]);

      result.backToBackGuest = [...result.backToBackGuest, ...backToBack.guestIds];
      result.backToBackOwner = [...result.backToBackOwner, ...backToBack.ownerIds];
      result.checkoutsOwner = [...result.checkoutsOwner, ...checkouts.ownerIds];
      result.checkinsOwner = [...result.checkinsOwner, ...checkins.ownerIds];
      result.checkoutsGuest = [...result.checkoutsGuest, ...checkouts.guestIds];
      result.checkinsGuest = [...result.checkinsGuest, ...checkins.guestIds];
      result.recent = [...result.recent, ...recent];
      result.hold = _.uniq([...result.hold, ...checkouts.holdIds, ...checkins.holdIds]);
    });

    return result;
  },
);

export const getHighlightedReservationsIds = createSelector(
  getFilters,
  getReservationIdsByTypes,
  ({ reservations }, types) => {
    let highlightedIds = [];
    if (_.includes(reservations, 'hasReservation')) {
      highlightedIds = types.hasReservation;
    }
    if (_.includes(reservations, 'checkinOnly')) {
      highlightedIds = [...highlightedIds, ...types.checkins];
    }
    if (_.includes(reservations, 'checkoutOnly')) {
      highlightedIds = [...highlightedIds, ...types.checkouts];
    }
    if (_.includes(reservations, 'backToBack')) {
      highlightedIds = [...highlightedIds, ...types.backToBack];
    }
    if (_.includes(reservations, 'backToBackGuest')) {
      highlightedIds = [...highlightedIds, ...types.backToBackGuest];
    }
    if (_.includes(reservations, 'backToBackOwner')) {
      highlightedIds = [...highlightedIds, ...types.backToBackOwner];
    }
    if (_.includes(reservations, 'checkinOwner')) {
      highlightedIds = [...highlightedIds, ...types.checkinsOwner];
    }
    if (_.includes(reservations, 'checkoutOwner')) {
      highlightedIds = [...highlightedIds, ...types.checkoutsOwner];
    }
    if (_.includes(reservations, 'recent')) {
      highlightedIds = [...highlightedIds, ...types.recent];
    }
    if (_.includes(reservations, 'hold')) {
      highlightedIds = [...highlightedIds, ...types.hold];
    }
    if (_.includes(reservations, 'checkinGuest')) {
      highlightedIds = [...highlightedIds, ...types.checkinsGuest];
    }
    if (_.includes(reservations, 'checkoutGuest')) {
      highlightedIds = [...highlightedIds, ...types.checkoutsGuest];
    }

    return highlightedIds;
  },
);

export const reservationShouldBeHighlighted = createSelector(
  getHighlightedReservationsIds,
  (_state, reservationId) => reservationId,
  (highlightedIds, reservationId) => _.includes(highlightedIds, reservationId),
);

const getReadinessCounts = (counts, reservation) => {
  if (reservation.ready) {
    incrementCounter(counts, 'readiness', 'ready');
  } else {
    incrementCounter(counts, 'readiness', 'notReady');
  }
};

// Filtered Property Ids
const getPropertyIdsByReservationFilters = (
  reservations,
  dates,
  propertyReservations,
  filterObject,
  filters,
  propertyIds,
) => {
  if (_.size(filters.reservations) === 0) {
    return propertyIds;
  }

  const reservationsByProperty = _.groupBy(reservations, 'home_id');

  return _.filter(propertyIds, propertyId => {
    if (_.size(_.get(propertyReservations, propertyId)) === 0) {
      return false;
    }
    const { backToBack, checkins, checkouts, recent, hasReservation } = groupReservations(
      reservationsByProperty[propertyId],
      dates,
    );

    const holds = _.uniqBy(_.concat([], checkins.hold, checkouts.hold), 'id');

    return (
      (filterObject.hasReservation && hasReservation) ||
      (filterObject.noReservation && !hasReservation) ||
      (filterObject.recent && _.size(recent)) ||
      (filterObject.backToBack && _.size(backToBack.all)) ||
      (filterObject.backToBackGuest && _.size(backToBack.guest)) ||
      (filterObject.backToBackOwner && _.size(backToBack.owner)) ||
      (filterObject.checkinOnly && _.size(checkins.all)) ||
      (filterObject.checkoutOnly && _.size(checkouts.all)) ||
      (filterObject.checkinOwner && _.size(checkins.owner) > 0) ||
      (filterObject.checkoutOwner && _.size(checkouts.owner) > 0) ||
      (filterObject.checkoutGuest && _.size(checkouts.guest) > 0) ||
      (filterObject.checkinGuest && _.size(checkins.guest) > 0) ||
      (filterObject.hold && _.size(holds) > 0)
    );
  });
};

const getPropertyIdsByReadiness = (nextReservations, filters, propertyIds) => {
  if (_.size(filters.readiness) === 0) {
    return propertyIds;
  }

  const readyFilter = _.includes(filters.readiness, 'ready');
  const notReadyFilter = _.includes(filters.readiness, 'notReady');

  if (readyFilter && notReadyFilter) {
    return propertyIds;
  }

  return _.filter(
    propertyIds,
    propertyId =>
      (readyFilter && propertyIsReady(nextReservations, propertyId) === true) ||
      (!readyFilter && propertyIsReady(nextReservations, propertyId) === false),
  );
};

/**
 *  Selectors for Schedule V2
 */

/**
 * @return {String}
 */
export const getSearchQuery = state => getFromScheduleConfig(state, 'searchQuery');

/**
 * @return {Moment}
 */
export const getActiveDate = state => getFromScheduleConfig(state, 'activeDate');

/**
 * @return {Boolean}
 */
export const getExpandAllFlag = state => getFromScheduleConfig(state, 'expandAll');

/**
 * @return {Boolean}
 */
export const getOrganizeModeFlag = state => getFromScheduleConfig(state, 'organizeModeFlag');

/**
 * @return {String}
 */
export const getSortingOption = state => getFromScheduleConfig(state, 'sortingOption');

/**
 * @return {String}
 */
export const getScheduleMode = state => getFromScheduleConfig(state, 'mode');

/**
 * @return {Object}
 */
export const getScheduleRangeDate = state => {
  const dateModifier = getScheduleMode(state) === SCHEDULE_MODE.WEEKLY ? 6 : 0;
  const startDate = getActiveDate(state);
  const endDate = calendarHelpers.modifyActiveDate(startDate, dateModifier);

  return {
    start_date: formatDate(startDate, null, SCHEDULE_ROUTE_DATE_FORMAT),
    end_date: formatDate(endDate, null, SCHEDULE_ROUTE_DATE_FORMAT),
  };
};

/**
 * @return {Boolean}
 */
export const isScheduleIsMobile = state => getScheduleMode(state) === SCHEDULE_MODE.MOBILE;

/**
 * @return {String}
 */
export const getScheduleSubject = state => getFromScheduleConfig(state, 'subject');

/**
 * @return {Boolean}
 */
export const getFiltersVisibility = state => getFromScheduleConfig(state, 'filtersVisible');

/**
 * @return {Boolean}
 */
export const getScheduleMounted = state => getFromScheduleConfig(state, 'scheduleMounted');

/**
 * @return {Boolean}
 */
export const getFetchingFlag = (state, key) =>
  getFromScheduleConfig(state, `fetching${_.upperFirst(key)}`);

/**
 * @return {Array.<Number>}
 */
export const getDisplayedTasksIds = state => getFromScheduleConfig(state, 'displayedTasksIds');

/**
 * @return {Object}
 */
export const getSchedulePagination = state => getFromScheduleConfig(state, 'pagination');

/**
 * @return {Array.<Object>}
 */
const getPropertyTasks = (propertyId, dateTasks, activeDate, mode, tasks) => {
  const formattedActiveDate = formatDate(activeDate, false, 'YYYY-MM-DD');
  let propertyTasksIds = [];

  switch (mode) {
    case SCHEDULE_MODE.DAILY:
    default: {
      propertyTasksIds = _.get(dateTasks, formattedActiveDate, []);

      break;
    }
    case SCHEDULE_MODE.WEEKLY: {
      let dayIterator = 0;

      for (dayIterator; dayIterator < SCHEDULE_DATE_MODIFIER.WEEKLY; dayIterator += 1) {
        const iteratedDate = calendarHelpers.modifyActiveDate(activeDate, dayIterator);
        const formattedIteratedDate = formatDate(iteratedDate, false, 'YYYY-MM-DD');
        const currentPropertyTasksIds = _.get(dateTasks, formattedIteratedDate, []);

        propertyTasksIds = [...propertyTasksIds, ...currentPropertyTasksIds];
      }

      break;
    }
  }

  return _.values(_.pick(tasks, propertyTasksIds));
};

/**
 * @return {Object}
 */
const getAllFilteredPropertyTasks = createSelector(
  getPropertyTasksByDate,
  getFilters,
  getActiveDate,
  getScheduleMode,
  datastore.selectors.getTasks,
  datastore.selectors.company.getCompanyGroups,
  (propertyTasksByDateIds, usedFilters, activeDate, mode, tasks, companyGroups) => {
    const propertyIdTasks = {};
    _.forEach(propertyTasksByDateIds, (dateTasks, propertyId) => {
      const propertyTasks = getPropertyTasks(propertyId, dateTasks, activeDate, mode, tasks);
      propertyIdTasks[propertyId] = ScheduleHelpers.applyScheduleFilters(
        propertyTasks,
        usedFilters,
        companyGroups,
      );
    });
    return propertyIdTasks;
  },
);

const getAllFilteredPropertyTaskDrafts = createSelector(
  getPropertyTaskDraftsByDate,
  getFilters,
  getActiveDate,
  getScheduleMode,
  datastore.selectors.getTaskDrafts,
  datastore.selectors.company.getCompanyGroups,
  (propertyTaskDraftsByDateIds, usedFilters, activeDate, mode, taskDrafts, companyGroups) => {
    const propertyIdTasks = {};
    _.forEach(propertyTaskDraftsByDateIds, (dateTasks, propertyId) => {
      const propertyTasks = getPropertyTasks(propertyId, dateTasks, activeDate, mode, taskDrafts);
      propertyIdTasks[propertyId] = ScheduleHelpers.applyScheduleFilters(
        propertyTasks,
        usedFilters,
        companyGroups,
      );
    });
    return propertyIdTasks;
  },
);

const getCompanyFilteredPropertyIds = createSelector(
  datastore.selectors.company.getHomes,
  datastore.selectors.company.getAllHomeTags,
  datastore.selectors.company.getCompanyGroups,
  getFilters,
  getSearchQuery,
  (homes, homeTags, companyGroups, filters, searchQuery) =>
    datastore.selectors.company.getFilteredPropertyIdsV2(
      homes,
      homeTags,
      companyGroups,
      filters,
      searchQuery,
    ),
);

const getFilteredPropertyIds = createSelector(
  datastore.selectors.getReservations,
  getActiveRangeDate,
  getPropertyReservationIds,
  getCompanyFilteredPropertyIds,
  getFilters,
  getSearchQuery,
  getFilteredTaskPropertyIds,
  getFilteredTaskDraftPropertyIds,
  getNextReservations,
  (
    reservations,
    dates,
    propertyReservations,
    companyFilteredPropertyIds,
    filters,
    searchQuery,
    filteredTaskPropertyIds,
    filteredTaskDraftPropertyIds,
    nextReservations,
  ) => {
    let filteredPropertyIds = companyFilteredPropertyIds;

    if (
      _.size(filters.statuses) > 0 ||
      _.size(filters.departments) > 0 ||
      _.size(filters.assignments) > 0 ||
      _.size(filters.assignmentStatus) > 0
    ) {
      // when filters are applied only show properties with tasks
      filteredPropertyIds = _.intersection(
        filteredPropertyIds,
        _.concat(filteredTaskPropertyIds, filteredTaskDraftPropertyIds),
      );
    }

    const filterObject = getFilterObject(filters, 'reservations');

    return getPropertyIdsByReservationFilters(
      reservations,
      dates,
      propertyReservations,
      filterObject,
      filters,
      getPropertyIdsByReadiness(nextReservations, filters, filteredPropertyIds),
    );
  },
);

export const getFilteredProperties = createSelector(
  datastore.selectors.getProperties,
  getFilteredPropertyIds,
  (properties, propertyIds) =>
    _.filter(_.map(propertyIds, propertyId => _.get(properties, propertyId))).sort(
      (current, next) => sorterFunction(_.toLower(current.display), _.toLower(next.display)),
    ),
);

export const getDraftTaskIds = createSelector(
  getFilteredPropertyIds,
  getFilters,
  datastore.selectors.getTaskDrafts,
  datastore.selectors.company.getCompanyGroups,
  getScheduleRangeDate,
  (filteredPropertIds, filters, drafts, companyGroups, dateRange) => {
    const { start_date: startDate, end_date: endDate } = dateRange;

    const draftsFilteredByTime = _.filter(drafts, task =>
      isDateInTimeframe(task.start_date, startDate, endDate),
    );

    const draftsFiltedByProperty = _.filter(draftsFilteredByTime, task =>
      _.includes(filteredPropertIds, task.home_id),
    );

    const filteredDrafts = ScheduleHelpers.applyScheduleFilters(
      draftsFiltedByProperty,
      filters,
      companyGroups,
    );

    return _.map(filteredDrafts, 'id');
  },
);

/**
 * @return {Array}
 */
export const getFilteredPropertyTasks = (state, propertyId) =>
  _.get(getAllFilteredPropertyTasks(state), propertyId, []);

export const getFilteredPropertyTaskDrafts = (state, propertyId) =>
  _.get(getAllFilteredPropertyTaskDrafts(state), propertyId, []);

/**
 * @return {Array.<Object>}
 */
const getPersonTasks = (dateTasks, activeDate, mode, tasks) => {
  const formattedActiveDate = formatDate(activeDate, false, 'YYYY-MM-DD');

  let personTasksIds = [];

  switch (mode) {
    case SCHEDULE_MODE.DAILY:
    default: {
      personTasksIds = _.get(dateTasks, formattedActiveDate, []);

      break;
    }
    case SCHEDULE_MODE.WEEKLY: {
      let dayIterator = 0;

      for (dayIterator; dayIterator < SCHEDULE_DATE_MODIFIER.WEEKLY; dayIterator += 1) {
        const iteratedDate = calendarHelpers.modifyActiveDate(activeDate, dayIterator);
        const formattedIteratedDate = formatDate(iteratedDate, false, 'YYYY-MM-DD');
        const currentPropertyTasksIds = _.get(dateTasks, formattedIteratedDate, []);

        personTasksIds = [...personTasksIds, ...currentPropertyTasksIds];
      }

      break;
    }
  }

  return _.values(_.pick(tasks, personTasksIds));
};

/**
 * @return {Object}
 */
const getAllFilteredPersonTasks = createSelector(
  getPersonTasksByDate,
  getFilters,
  getActiveDate,
  getScheduleMode,
  datastore.selectors.getTasks,
  datastore.selectors.company.getCompanyGroups,
  (personTasksByDateIds, usedFilters, activeDate, mode, tasks, companyGroups) => {
    const personIdTasks = {};
    _.forEach(personTasksByDateIds, (dateTasks, personId) => {
      const personTasks = getPersonTasks(dateTasks, activeDate, mode, tasks);
      personIdTasks[personId] = ScheduleHelpers.applyScheduleFilters(
        personTasks,
        usedFilters,
        companyGroups,
      );
    });
    return personIdTasks;
  },
);

const getAllFilteredPersonTaskDrafts = createSelector(
  getPersonTaskDraftsByDate,
  getFilters,
  getActiveDate,
  getScheduleMode,
  datastore.selectors.getTaskDrafts,
  datastore.selectors.company.getCompanyGroups,
  (personTaskDraftsByDateIds, usedFilters, activeDate, mode, taskDrafts, companyGroups) => {
    const personIdTasks = {};
    _.forEach(personTaskDraftsByDateIds, (dateTasks, personId) => {
      const personTasks = getPersonTasks(dateTasks, activeDate, mode, taskDrafts);
      personIdTasks[personId] = ScheduleHelpers.applyScheduleFilters(
        personTasks,
        usedFilters,
        companyGroups,
      );
    });
    return personIdTasks;
  },
);

const getPersonTaskStats = (taskIds, tasks, draftIds, draftTasks) => {
  const taskCount = _.size(taskIds) + _.size(draftIds);
  const totalTime = moment.duration();

  _.forEach(taskIds, taskId => {
    const taskTime = _.get(tasks, [taskId, 'estimate', 'estimated_time']);
    if (!_.isEmpty(taskTime)) {
      totalTime.add(taskTime);
    }
  });

  _.forEach(draftIds, draftId => {
    const taskTime = _.get(draftTasks, [draftId, 'estimate', 'estimated_time']);
    if (!_.isEmpty(taskTime)) {
      totalTime.add(taskTime);
    }
  });

  return { count: taskCount, time: totalTime };
};

const getAllPeopleTaskAssignmentStats = createSelector(
  getPersonTasksByDate,
  getPersonTaskDraftsByDate,
  datastore.selectors.getTasks,
  datastore.selectors.getTaskDrafts,
  (personTasksByDateIds, personTaskDraftsByDateIds, tasks, taskDrafts) => {
    const personIdTaskStats = {};
    const personIds = _.union(_.keys(personTasksByDateIds), _.keys(personTaskDraftsByDateIds));
    _.forEach(personIds, personId => {
      personIdTaskStats[personId] = {};
      const personTasks = _.get(personTasksByDateIds, personId, {});
      const personDraftTasks = _.get(personTaskDraftsByDateIds, personId, {});

      _.forEach(_.union(_.keys(personTasks), _.keys(personDraftTasks)), taskDate => {
        personIdTaskStats[personId][taskDate] = getPersonTaskStats(
          _.get(personTasks, taskDate),
          tasks,
          _.get(personDraftTasks, taskDate),
          taskDrafts,
        );
      });
    });
    return personIdTaskStats;
  },
);

/**
 * @return {Array}
 */
export const getFilteredPersonTasks = (state, personId) =>
  _.get(getAllFilteredPersonTasks(state), personId, []);

export const getFilteredUnassignedTasks = state =>
  _.get(getAllFilteredPersonTasks(state), SCHEDULE_UNASSIGNED_ID, []);
export const getFilteredUnassignedTasksDrafts = state =>
  _.get(getAllFilteredPersonTaskDrafts(state), SCHEDULE_UNASSIGNED_ID, []);
export const getFilteredUnassignedTasksSize = state => _.size(getFilteredUnassignedTasks(state));
export const getFilteredUnassignedTasksDraftsSize = state =>
  _.size(getFilteredUnassignedTasksDrafts(state));
export const getFilteredPersonTaskDrafts = (state, personId) =>
  _.get(getAllFilteredPersonTaskDrafts(state), personId, []);

export const getPersonTaskAssignmentStats = (state, personId, date) =>
  _.get(getAllPeopleTaskAssignmentStats(state), [personId, date]);

const setPlanningPersonCounts = (counts, planningPeople) => {
  incrementCounter(
    counts,
    'availability',
    TASK_AVAILABILITY_STATUS.HAS_AVAILABILITY,
    _.size(planningPeople.availablePeople),
  );
  incrementCounter(
    counts,
    'availability',
    TASK_AVAILABILITY_STATUS.OVERBOOKED,
    _.size(planningPeople.overbookedPeople),
  );
  incrementCounter(
    counts,
    'availability',
    TASK_AVAILABILITY_STATUS.NEAR_CAPACITY,
    _.size(planningPeople.nearCapacityPeople),
  );
  incrementCounter(
    counts,
    'availability',
    TASK_AVAILABILITY_STATUS.NO_AVAILABILITY_SET,
    _.size(planningPeople.noAvailablePeople),
  );
};

/**
 * @param {Object} state
 * @return {Boolean}
 */
const shouldFilterPeople = state => {
  const { assignments, departments, groups, statuses } = getFilters(state);

  return !!_.size(assignments) || !!_.size(departments) || !!_.size(statuses) || !!_.size(groups);
};

/**
 * @param {Boolean} getPartners
 * @param {Object} state
 * @return {Array.<Object>}
 */
const getFilteredActivePeople = state => {
  let activePeople = datastore.selectors.company.getActivePeople(state);

  const {
    assignments: assignmentsFilter,
    groups: groupsFilter,
    userDepartments: userDepartmentsFilter,
  } = getFilters(state);

  const isPersonSchedule = isPersonTypeSchedule();
  const hasAssignmentFilter = _.size(assignmentsFilter) > 0;
  const hasUserDepartmentsFilter = _.size(userDepartmentsFilter) > 0;
  const hasGroupsFilter = _.size(groupsFilter) > 0;
  if (isPersonSchedule) {
    const filteredPropertiesIds = getFilteredPropertyIds(state);

    if (hasAssignmentFilter) {
      activePeople = _.filter(activePeople, person => _.includes(assignmentsFilter, person.id));
    }

    if (hasUserDepartmentsFilter) {
      activePeople = _.filter(activePeople, person => {
        const personDepartments = _.get(person, 'type_departments', []);

        return _.some(personDepartments, department =>
          _.includes(userDepartmentsFilter, _.get(department, 'code', '')),
        );
      });
    }

    if (hasGroupsFilter) {
      activePeople = _.filter(activePeople, person => {
        const personTasks = _.filter(
          [
            ...getFilteredPersonTasks(state, person.id),
            ...getFilteredPersonTaskDrafts(state, person.id),
          ],
          task => _.includes(filteredPropertiesIds, getTaskHomeId(task)),
        );

        const personHasTasks = _.size(personTasks) > 0;
        const hasAllAccess = _.get(person, 'group_all_access', false);
        const isInGroup = _.some(_.get(person, 'groups', []), group =>
          _.includes(groupsFilter, _.get(group, 'id', '')),
        );

        return personHasTasks || hasAllAccess || isInGroup;
      });
    }
  } else if (shouldFilterPeople(state)) {
    return _.filter(
      activePeople,
      person =>
        _.size(getFilteredPersonTasks(state, person.id)) > 0 ||
        _.size(getFilteredPersonTaskDrafts(state, person.id)) > 0,
    );
  }

  return activePeople;
};

export const getFilteredPlanningPeople = (filters, activeDate, planningPeople) => {
  let filteredPeople = [
    {
      id: SCHEDULE_UNASSIGNED_ID,
      full_name: _.upperFirst(SCHEDULE_UNASSIGNED_ID),
    },
  ];

  _.each(filters.availability, availabilityFilter => {
    if (availabilityFilter === TASK_AVAILABILITY_STATUS.HAS_AVAILABILITY) {
      filteredPeople = _.concat(filteredPeople, planningPeople.availablePeople);
    }

    if (availabilityFilter === TASK_AVAILABILITY_STATUS.OVERBOOKED) {
      filteredPeople = _.concat(filteredPeople, planningPeople.overbookedPeople);
    }

    if (availabilityFilter === TASK_AVAILABILITY_STATUS.NEAR_CAPACITY) {
      filteredPeople = _.concat(filteredPeople, planningPeople.nearCapacityPeople);
    }

    if (availabilityFilter === TASK_AVAILABILITY_STATUS.NO_AVAILABILITY_SET) {
      filteredPeople = _.concat(filteredPeople, planningPeople.noAvailablePeople);
    }
  });

  return _.uniqBy(filteredPeople, 'id');
};

const moveUnassignedToTop = items =>
  _.chain(items)
    .partition({ id: 'unassigned' })
    .flatten()
    .value();

export const sortFilteredPeopleByCapacity = (state, people, sortingOption) => {
  const activeDate = getActiveDate(state);
  const getDailyDate = formatDate(activeDate, false, 'YYYY-MM-DD');
  const getDailyDateByDOW = _.lowerCase(formatDate(activeDate, false, 'dddd'));

  const peopleCapacity = [];
  _.forEach(people, person => {
    let capacityTime = 0;
    const personAvailabiltyTime = getPersonAvailabilityByDOWFromSixteenHours(
      state,
      person.id,
      getDailyDateByDOW,
    );

    const personAssignmentStats = getPersonTaskAssignmentStats(state, person.id, getDailyDate);
    const userIsOverbooked = isOverbooked(personAvailabiltyTime, personAssignmentStats);
    const taskTime = _.get(personAssignmentStats, 'time')
      ? moment.duration(personAssignmentStats.time, 'miliseconds').asMinutes()
      : 0;

    if (!_.isEmpty(personAvailabiltyTime)) {
      _.forEach(personAvailabiltyTime, time => {
        capacityTime += moment(time.end, 'HH:mm:ss').diff(
          moment(time.start, 'HH:mm:ss'),
          'minutes',
        );
      });
    }

    if (capacityTime && taskTime) {
      capacityTime -= taskTime;
    }

    peopleCapacity.push({
      userId: person.id,
      capacityTime,
      userIsOverbooked,
    });
  });

  const peopleWithCapacityTime = _.filter(peopleCapacity, person => person.capacityTime !== 0);
  const peopleWithoutCapacityTime = _.filter(peopleCapacity, person => person.capacityTime === 0);

  switch (sortingOption) {
    case 'capacity_asc': {
      const sortedPeople = [];
      const sortedUserAvailabilty = _.concat(
        _.orderBy(peopleWithCapacityTime, ['userIsOverbooked', 'capacityTime'], ['asc', 'asc']),
        peopleWithoutCapacityTime,
      );

      _.forEach(_.map(sortedUserAvailabilty, 'userId'), id =>
        sortedPeople.push(_.find(people, person => person.id === id)),
      );

      return moveUnassignedToTop(sortedPeople);
    }
    case 'capacity_desc': {
      const sortedPeople = [];
      const sortedUserAvailabilty = _.concat(
        _.orderBy(peopleWithCapacityTime, ['userIsOverbooked', 'capacityTime'], ['desc', 'desc']),
        peopleWithoutCapacityTime,
      );
      _.forEach(_.map(sortedUserAvailabilty, 'userId'), id =>
        sortedPeople.push(_.find(people, person => person.id === id)),
      );
      return moveUnassignedToTop(sortedPeople);
    }
    default:
      return people;
  }
};

/**
 * @return {Object}
 */
export const getFilteredPeople = createSelector(
  getFilteredActivePeople,
  getSearchQuery,
  getFilters,
  (people, searchQuery, filters) => {
    if (people) {
      const { assignments } = filters;
      let peopleArray = filterAssignable([...people]);

      if (_.isEmpty(assignments) || _.includes(assignments, 'notAssigned')) {
        peopleArray = [
          ...peopleArray,
          {
            id: SCHEDULE_UNASSIGNED_ID,
            full_name: _.upperFirst(SCHEDULE_UNASSIGNED_ID),
          },
        ];
      }

      const searchResults = searchObjects(peopleArray, searchQuery, [personName, 'email']);

      const orderedData = _.orderBy(
        searchResults,
        [person => personName(person).toLowerCase()],
        ['asc'],
      );

      const unassignedIndex = _.findIndex(orderedData, item => item.id === SCHEDULE_UNASSIGNED_ID);

      /* move item with id equal unassigned on the top */
      if (unassignedIndex > -1) {
        const unassignedItem = orderedData[unassignedIndex];
        orderedData.splice(unassignedIndex, 1);
        return [unassignedItem, ...orderedData];
      }

      return orderedData;
    }

    return [];
  },
);

const getPlanningPeople = createSelector(
  getFilteredPeople,
  getAllPeopleAvailabilityFromSixteenHours,
  getAllPeopleTaskAssignmentStats,
  getActiveDate,
  (people, allPersonAvail, allPersonAssignmentStats, activeDate) => {
    const dailyDate = formatDate(activeDate, false, 'YYYY-MM-DD');
    const dailyDOWDate = _.lowerCase(formatDate(activeDate, false, 'dddd'));

    const availablePeople = [];
    const overbookedPeople = [];
    const noAvailablePeople = [];
    const nearCapacityPeople = [];

    _.each(people, person => {
      if (person) {
        const { id: personId } = person;
        const personDOWAvailability = _.get(allPersonAvail, [personId, dailyDOWDate]);
        const personAssignmentStats = _.get(allPersonAssignmentStats, [personId, dailyDate]);

        const personIsOverbooked =
          isNotAvailable(personDOWAvailability, personAssignmentStats) ||
          isOverbooked(personDOWAvailability, personAssignmentStats);
        const personIsNearCapacity = isNearCapacity(personDOWAvailability, personAssignmentStats);
        const noAvailabilitySet = _.isEmpty(personDOWAvailability);

        if (!noAvailabilitySet && !personIsOverbooked) {
          availablePeople.push(person);
        }

        if (personIsOverbooked) {
          overbookedPeople.push(person);
        }

        if (personIsNearCapacity) {
          nearCapacityPeople.push(person);
        }

        if (noAvailabilitySet) {
          noAvailablePeople.push(person);
        }
      }
    });

    return {
      availablePeople,
      nearCapacityPeople,
      noAvailablePeople,
      overbookedPeople,
    };
  },
);

export const getScheduleFilterCounts = createSelector(
  datastore.selectors.getTasks,
  datastore.selectors.getTaskDrafts,
  datastore.selectors.getProperties,
  datastore.selectors.getReservations,
  getNextReservations,
  datastore.selectors.company.getAllHomeTags,
  getActiveRangeDate,
  getPlanningPeople,
  datastore.selectors.company.getCompanyGroups,
  datastore.selectors.company.getActivePeople,
  getFilters,
  getAllFilteredPersonTasks,
  getAllFilteredPersonTaskDrafts,
  (
    tasks,
    taskDrafts,
    properties,
    reservations,
    nextReservations,
    homeTags,
    dates,
    planningPeople,
    companyGroups,
    activePeople,
    filters,
    filteredPersonTasks,
    filteredPersonTaskDrafts,
  ) => {
    const counts = {};
    _.forEach(tasks, task =>
      getScheduleFilterCount(counts, task, properties, homeTags, companyGroups),
    );
    _.forEach(taskDrafts, taskDraft =>
      getScheduleFilterCount(counts, taskDraft, properties, homeTags, companyGroups),
    );
    _.forEach(nextReservations, reservation => getReadinessCounts(counts, reservation));

    setPlanningPersonCounts(counts, planningPeople);

    if (isPersonTypeSchedule()) {
      setPersonTypeScheduleCounts(
        counts,
        activePeople,
        companyGroups,
        filters,
        filteredPersonTasks,
        filteredPersonTaskDrafts,
        tasks,
        taskDrafts,
        companyGroups,
      );
      setPlanningReservationCounts(
        counts,
        reservations,
        dates,
        filteredPersonTasks,
        filteredPersonTaskDrafts,
      );
    } else {
      setReservationCounts(counts, reservations, dates);
    }

    return counts;
  },
);

export const getPeopleAndTasks = createSelector(
  getFilters,
  getFilteredPeople,
  getAllFilteredPersonTasks,
  getAllFilteredPersonTaskDrafts,
  getFilteredPropertyIds,
  (filters, people, allFilteredTasks, allFilteredTaskDrafts, filteredPropertiesIds) => {
    const hasBuildingsFilters = !_.isEmpty(_.get(filters, 'buildings', []));
    const hasTagsFilters = !_.isEmpty(_.get(filters, 'tags', []));
    const peopleRelated = {};

    _.each(people, person => {
      if (person) {
        const { id: personId } = person;
        let personTasks = [
          ..._.get(allFilteredTasks, personId, []),
          ..._.get(allFilteredTaskDrafts, personId, []),
        ];

        if (hasBuildingsFilters || hasTagsFilters) {
          personTasks = _.filter(personTasks, personTask => {
            const propertyId = getTaskHomeId(personTask);

            return _.includes(filteredPropertiesIds, propertyId);
          });
        }

        peopleRelated[personId] = {
          tasks: sortTasks(personTasks),
        };
      }
    });

    return {
      people,
      peopleRelated,
    };
  },
);

export const getPlanningPeopleData = state =>
  createSelector(
    getFilters,
    getActiveDate,
    getFilteredPeople,
    getFilteredPropertyIds,
    getPlanningPeople,
    getAllFilteredPersonTasks,
    getAllFilteredPersonTaskDrafts,
    getAllPropertyReservations,
    getSortingOption,
    (
      filters,
      activeDate,
      people,
      filteredPropertiesIds,
      planingPeople,
      allFilteredTasks,
      allFilteredTaskDrafts,
      propertyReservations,
      sortingOption,
    ) => {
      const rawTasks = {};
      const rawReservations = {};
      const hasReservationFilters = !_.isEmpty(_.get(filters, 'reservations', []));
      const hasBuildingsFilters = !_.isEmpty(_.get(filters, 'buildings', []));
      const hasTagsFilters = !_.isEmpty(_.get(filters, 'tags', []));
      const filterByProperty = hasReservationFilters || hasBuildingsFilters || hasTagsFilters;

      let filteredPeople =
        _.size(filters.availability) > 0
          ? getFilteredPlanningPeople(filters, activeDate, planingPeople)
          : people;

      filteredPeople = sortFilteredPeopleByCapacity(state, filteredPeople, sortingOption);

      _.each(filteredPeople, person => {
        if (person) {
          const { id: personId } = person;
          let allPersonTasks = [
            ..._.get(allFilteredTasks, personId, []),
            ..._.get(allFilteredTaskDrafts, personId, []),
          ];

          if (filterByProperty) {
            allPersonTasks = _.filter(allPersonTasks, task => {
              const propertyId = getTaskHomeId(task);

              return _.includes(filteredPropertiesIds, propertyId);
            });
          }

          _.forEach(allPersonTasks, task => {
            const propertyId = getTaskHomeId(task);

            if (propertyId && !rawReservations[propertyId]) {
              rawReservations[propertyId] = _.get(propertyReservations, propertyId);
            }
          });

          rawTasks[personId] = sortTasks(allPersonTasks);
        }
      });

      return {
        reservations: rawReservations,
        people: filteredPeople,
        tasks: rawTasks,
      };
    },
  )(state);

export const isPeopleAreOverbooked = createSelector(
  getFilteredPeople,
  getAllPeopleAvailabilityFromSixteenHours,
  getAllPeopleTaskAssignmentStats,
  getActiveDate,
  (people, allPersonAvail, allPersonAssignmentStats, activeDate) => {
    const dailyDate = formatDate(activeDate, false, 'YYYY-MM-DD');
    const dateByDOW = _.lowerCase(formatDate(activeDate, false, 'dddd'));

    const personAvailability = personId => _.get(allPersonAvail, [personId, dateByDOW]);
    const personTaskAssignment = personId => _.get(allPersonAssignmentStats, [personId, dailyDate]);
    return _.some(people, person => {
      const personId = _.get(person, 'id');
      return (
        personId !== 'unassigned' &&
        isOverbooked(personAvailability(personId), personTaskAssignment(personId))
      );
    });
  },
);

export const isAvailabilityConflict = createSelector(
  getFilteredPeople,
  getAllPeopleAvailabilityFromSixteenHours,
  getAllPeopleTaskAssignmentStats,
  getActiveDate,
  (people, allPersonAvail, allPersonAssignmentStats, activeDate) => {
    const dailyDate = formatDate(activeDate, false, 'YYYY-MM-DD');
    const dateByDOW = _.lowerCase(formatDate(activeDate, false, 'dddd'));

    const personAvailability = personId => _.get(allPersonAvail, [personId, dateByDOW]);
    const personTaskAssignment = personId => _.get(allPersonAssignmentStats, [personId, dailyDate]);
    return _.some(people, person => {
      const personId = _.get(person, 'id');
      const personAvail = personAvailability(personId);
      const personTask = personTaskAssignment(personId);
      return (
        personId !== 'unassigned' &&
        (isNotAvailable(personAvail, personTask) || isOverbooked(personAvail, personTask))
      );
    });
  },
);

export const getActivePropertyFilters = createSelector(
  getFilters,
  filters => getActiveFilters(filters, PROPERTY_FILTERS_NAMES_SCHEDULE),
);

export const getActiveTasksFilters = createSelector(
  getFilters,
  filters => getActiveFilters(filters, VALID_TASK_FILTERS),
);

// Get properties filtered by tasks
export const getPropertiesFilteredByTasks = createSelector(
  datastore.selectors.getProperties,
  getFilteredTaskPropertyIds,
  getActiveTasksFilters,
  (propertiesObject, filteredPropertiesIds, taskFilters) => {
    const hasTaskFilters = _.size(taskFilters) > 0;

    if (hasTaskFilters) {
      return _.pick(propertiesObject, filteredPropertiesIds);
    }

    return propertiesObject;
  },
);

// Get properties filtered by drafts
export const getPropertiesFilteredByDrafts = createSelector(
  datastore.selectors.getProperties,
  getFilteredTaskDraftPropertyIds,
  getActiveTasksFilters,
  (propertiesObject, filteredPropertiesIds, taskFilters) => {
    const hasTaskFilters = _.size(taskFilters) > 0;

    if (hasTaskFilters) {
      return _.pick(propertiesObject, filteredPropertiesIds);
    }

    return propertiesObject;
  },
);

// Get properties with applied filters
export const getScheduleFilteredProperties = createSelector(
  getPropertiesFilteredByTasks,
  getPropertiesFilteredByDrafts,
  datastore.selectors.getReservations,
  datastore.selectors.company.getAllHomeTags,
  getActivePropertyFilters,
  getActiveRangeDate,
  getNextReservations,
  getSearchQuery,
  datastore.selectors.company.getCompanyGroups,
  (
    propertiesTaskObject,
    propertiesDraftsObject,
    reservations,
    propertiesTags,
    propertyFilters,
    dateRange,
    nextReservations,
    searchQuery,
    companyGroups,
  ) => {
    // Properties data
    const propertiesArray = _.values({ ...propertiesTaskObject, ...propertiesDraftsObject });
    const reservationsByProperty = _.groupBy(reservations, 'home_id');

    // Filter properties
    const filteredPropertiesArray = _.filter(propertiesArray, property => {
      // Single property data
      const propertyId = _.get(property, 'id');
      const propertyReservations = _.get(reservationsByProperty, propertyId, []);
      const propertyNextReservation = _.get(nextReservations, propertyId, {});
      const propertyTags = _.get(propertiesTags, propertyId, []);

      // Apply filters on property
      return !shouldPropertyBeFiltered({
        property,
        propertyFilters,
        propertyTags,
        propertyNextReservation,
        propertyReservations,
        dateRange,
        companyGroups,
      });
    });

    if (!_.isEmpty(searchQuery)) {
      return searchObjects(filteredPropertiesArray, searchQuery, [
        'name',
        'address1',
        'city',
        'marketing_code',
        'internal_code',
      ]);
    }

    return filteredPropertiesArray;
  },
);

// Get sorted properties with applied filters
export const getScheduleSortedProperties = createSelector(
  getScheduleFilteredProperties,
  filteredPropertiesArray => {
    // Use natural sort order alghoritm
    const sortingAlghoritm = natsort();

    // Sort properties by display name ASC
    return filteredPropertiesArray.sort((currentProperty, nextProperty) => {
      const currentPropertyDisplay = _.toLower(currentProperty.display);
      const nextPropertyDisplay = _.toLower(nextProperty.display);

      return sortingAlghoritm(currentPropertyDisplay, nextPropertyDisplay);
    });
  },
);

export const getPropertiesCount = createSelector(
  datastore.selectors.getProperties,
  properties => _.size(_.keys(properties)),
);

export const getDisplayedPropertiesCount = createSelector(
  getFilteredProperties,
  properties => _.size(properties),
);

export const isPaginationEnabled = createSelector(
  getPropertiesCount,
  getDisplayedPropertiesCount,
  getScheduleSubject,
  (propertiesCount, displayedPropertiesCount, subjectType) => {
    const isPropertySchedule = subjectType === SCHEDULE_SUBJECT.PROPERTY;
    const areEnoughProperties = propertiesCount >= PAGINATION_SETTINGS.MIN_RECORDS_TO_ENABLE;
    const areEnoughPropertiesDisplay = displayedPropertiesCount >= PAGINATION_SETTINGS.PER_PAGE;

    return isPropertySchedule && areEnoughProperties && areEnoughPropertiesDisplay;
  },
);
